// batch-66 inkoop-afwijkingen (FO-490) — afwijkingen-register op inkoopfacturen.
//
// Eén register voor alles wat er aan een inkoopfactuur niet klopt: per factuur
// of per regel, met een vaste soort, een betwist bedrag (excl. btw, Decimal in
// de DB), bewijs, een status en een eigenaar. De 3-way-match-oordelen (FO-393)
// en de extractie-markeringen "duplicaat" en "verkeerde BV" landen in hetzelfde
// register, zodat er één werkvoorraad is. De betwiste status van een factuur
// volgt uit dit register (de losse status BETWIST is niet meer te kiezen).
//
// Dit bestand bevat de gedeelde enums, labels, Zod-schema's (API én formulier
// valideren met hetzelfde schema) en DTO's. De pure regels (statusmachine,
// bedragberekening, betaalblokkade, creditnota-matcher) staan in @streep/utils.

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (mirror van de Prisma-enums) ──────────────────────────────────────

export const INKOOP_AFWIJKING_SOORTEN = [
  'PRIJS',
  'AANTAL_TE_VEEL',
  'AANTAL_TE_WEINIG',
  'NIET_GELEVERD',
  'DUBBEL',
  'VERKEERDE_BV',
  'VERKEERDE_BTW',
  'KWALITEIT_RETOUR',
  'KORTING_ONTBREEKT',
  'OVERIG',
] as const
export type InkoopAfwijkingSoort = (typeof INKOOP_AFWIJKING_SOORTEN)[number]

export const INKOOP_AFWIJKING_SOORT_LABELS: Record<InkoopAfwijkingSoort, string> = {
  PRIJS: 'Prijs wijkt af',
  AANTAL_TE_VEEL: 'Te veel gefactureerd',
  AANTAL_TE_WEINIG: 'Te weinig geleverd',
  NIET_GELEVERD: 'Niet geleverd',
  DUBBEL: 'Dubbel gefactureerd',
  VERKEERDE_BV: 'Verkeerde BV',
  VERKEERDE_BTW: 'Verkeerde btw',
  KWALITEIT_RETOUR: 'Kwaliteit of retour',
  KORTING_ONTBREEKT: 'Korting ontbreekt',
  OVERIG: 'Overig',
}

/**
 * Welke invoer een soort vraagt om het afwijkingsbedrag te berekenen:
 *  - PRIJS: afgesproken en gefactureerde stuksprijs, maal het aantal;
 *  - AANTAL: verwacht en gefactureerd aantal, maal de stuksprijs;
 *  - RETOUR: het geretourneerde aantal maal de stuksprijs;
 *  - BTW: verwacht en gefactureerd btw-percentage over het regel- of factuurbedrag;
 *  - GEHEEL: het hele regel- of factuurbedrag (dubbel, verkeerde BV);
 *  - VRIJ: alleen een handmatig bedrag.
 */
export type InkoopAfwijkingInvoerSoort = 'PRIJS' | 'AANTAL' | 'RETOUR' | 'BTW' | 'GEHEEL' | 'VRIJ'

export const INKOOP_AFWIJKING_INVOER_SOORT: Record<InkoopAfwijkingSoort, InkoopAfwijkingInvoerSoort> = {
  PRIJS: 'PRIJS',
  KORTING_ONTBREEKT: 'PRIJS',
  AANTAL_TE_VEEL: 'AANTAL',
  AANTAL_TE_WEINIG: 'AANTAL',
  NIET_GELEVERD: 'AANTAL',
  KWALITEIT_RETOUR: 'RETOUR',
  VERKEERDE_BTW: 'BTW',
  DUBBEL: 'GEHEEL',
  VERKEERDE_BV: 'GEHEEL',
  OVERIG: 'VRIJ',
}

/**
 * De soorten die betaling standaard blokkeren (FO-490 A2): prijs, aantal, niet
 * geleverd en dubbel. Instelbaar via de instellingen van het register; deze
 * lijst is de terugval zolang er geen instelling is opgeslagen.
 */
export const INKOOP_AFWIJKING_STANDAARD_BLOKKEREND = [
  'PRIJS',
  'AANTAL_TE_VEEL',
  'AANTAL_TE_WEINIG',
  'NIET_GELEVERD',
  'DUBBEL',
] as const satisfies readonly InkoopAfwijkingSoort[]

export const INKOOP_AFWIJKING_STATUSSEN = [
  'OPEN',
  'GEMELD',
  'IN_BEHANDELING_LEVERANCIER',
  'CREDITNOTA_VERWACHT',
  'OPGELOST',
] as const
export type InkoopAfwijkingStatus = (typeof INKOOP_AFWIJKING_STATUSSEN)[number]

export const INKOOP_AFWIJKING_STATUS_LABELS: Record<InkoopAfwijkingStatus, string> = {
  OPEN: 'Open',
  GEMELD: 'Gemeld',
  IN_BEHANDELING_LEVERANCIER: 'In behandeling bij leverancier',
  CREDITNOTA_VERWACHT: 'Creditnota verwacht',
  OPGELOST: 'Opgelost',
}

export const INKOOP_AFWIJKING_STATUS_BADGE_VARIANT: Record<
  InkoopAfwijkingStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  OPEN: 'danger',
  GEMELD: 'warning',
  IN_BEHANDELING_LEVERANCIER: 'info',
  CREDITNOTA_VERWACHT: 'info',
  OPGELOST: 'success',
}

/** De statussen waarin een afwijking nog openstaat (alles behalve OPGELOST). */
export const INKOOP_AFWIJKING_OPEN_STATUSSEN = [
  'OPEN',
  'GEMELD',
  'IN_BEHANDELING_LEVERANCIER',
  'CREDITNOTA_VERWACHT',
] as const satisfies readonly InkoopAfwijkingStatus[]

export const INKOOP_AFWIJKING_UITKOMSTEN = [
  'CREDITNOTA',
  'CORRECTIEFACTUUR',
  'GEACCEPTEERD_MET_REDEN',
  'INGETROKKEN',
] as const
export type InkoopAfwijkingUitkomst = (typeof INKOOP_AFWIJKING_UITKOMSTEN)[number]

export const INKOOP_AFWIJKING_UITKOMST_LABELS: Record<InkoopAfwijkingUitkomst, string> = {
  CREDITNOTA: 'Creditnota ontvangen',
  CORRECTIEFACTUUR: 'Correctiefactuur ontvangen',
  GEACCEPTEERD_MET_REDEN: 'Geaccepteerd met reden',
  INGETROKKEN: 'Ingetrokken',
}

export const INKOOP_AFWIJKING_BRONNEN = ['HANDMATIG', 'MATCH', 'EXTRACTIE'] as const
export type InkoopAfwijkingBron = (typeof INKOOP_AFWIJKING_BRONNEN)[number]

export const INKOOP_AFWIJKING_BRON_LABELS: Record<InkoopAfwijkingBron, string> = {
  HANDMATIG: 'Handmatig',
  MATCH: '3-way-match',
  EXTRACTIE: 'Extractie',
}

export const INKOOP_AFWIJKING_DOCUMENT_SOORTEN = ['BEWIJS', 'MAIL'] as const
export type InkoopAfwijkingDocumentSoort = (typeof INKOOP_AFWIJKING_DOCUMENT_SOORTEN)[number]

export const INKOOP_AFWIJKING_BERICHT_RICHTINGEN = ['UITGAAND', 'INKOMEND', 'INTERN'] as const
export type InkoopAfwijkingBerichtRichting = (typeof INKOOP_AFWIJKING_BERICHT_RICHTINGEN)[number]

/**
 * `InkoopAfwijkingAudit.actie` is in de DB een vrije string; deze lijst houdt
 * hem consistent (de service schrijft alleen deze waarden).
 */
export const INKOOP_AFWIJKING_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'EIGENAAR_GEWIJZIGD',
  'STATUS_GEWIJZIGD',
  'GEMELD',
  'HERINNERD',
  'INTERN_GEMELD',
  'MELDEN_GEBLOKKEERD',
  'REACTIE_ONTVANGEN',
  'CREDITNOTA_GEKOPPELD',
  'DOCUMENT_TOEGEVOEGD',
  'DOCUMENT_VERWIJDERD',
  'REGEL_HERANKERD',
  'MATCH_BIJGEWERKT',
] as const
export type InkoopAfwijkingAuditActie = (typeof INKOOP_AFWIJKING_AUDIT_ACTIES)[number]

export const INKOOP_AFWIJKING_AUDIT_ACTIE_LABELS: Record<InkoopAfwijkingAuditActie, string> = {
  AANGEMAAKT: 'Geregistreerd',
  BIJGEWERKT: 'Bijgewerkt',
  EIGENAAR_GEWIJZIGD: 'Eigenaar gewijzigd',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  GEMELD: 'Gemeld aan leverancier',
  HERINNERD: 'Herinnering verstuurd',
  INTERN_GEMELD: 'Intern gemeld',
  MELDEN_GEBLOKKEERD: 'Melden geblokkeerd',
  REACTIE_ONTVANGEN: 'Reactie van leverancier',
  CREDITNOTA_GEKOPPELD: 'Creditnota gekoppeld',
  DOCUMENT_TOEGEVOEGD: 'Document toegevoegd',
  DOCUMENT_VERWIJDERD: 'Document verwijderd',
  REGEL_HERANKERD: 'Factuurregel gewijzigd',
  MATCH_BIJGEWERKT: 'Bijgewerkt door de 3-way-match',
}

// ─── Mail aan de leverancier: beheerbaar sjabloon ────────────────────────────

/**
 * De velden die in het onderwerp en de tekst van de mail aan de leverancier
 * gebruikt mogen worden. `[referentie]` is verplicht in het onderwerp: daarop
 * koppelt het systeem de reactie van de leverancier terug.
 */
export const INKOOP_AFWIJKING_MAIL_VELDEN = [
  { veld: '[referentie]', uitleg: 'De afwijkingsreferentie (AFW-jaar-volgnummer)' },
  { veld: '[factuurnummer]', uitleg: 'Het factuurnummer van de leverancier' },
  { veld: '[factuurdatum]', uitleg: 'De factuurdatum (dd-mm-jjjj)' },
  { veld: '[leverancier]', uitleg: 'De naam van de leverancier' },
  { veld: '[bv]', uitleg: 'De STREEP-BV waaraan de factuur gericht is' },
  { veld: '[afwijkingen]', uitleg: 'De lijst met afwijkingen (soort, regel, bedrag, toelichting)' },
  { veld: '[totaalbedrag]', uitleg: 'Het totale betwiste bedrag exclusief btw' },
  { veld: '[afzender]', uitleg: 'De naam van degene die de melding verstuurt' },
] as const

export const INKOOP_AFWIJKING_MAIL_REFERENTIE_VELD = '[referentie]'

// ─── Zod-schema's ────────────────────────────────────────────────────────────

const waarde = z
  .number({ message: 'Vul een geldig getal in' })
  .finite('Vul een geldig getal in')
  .min(0, 'De waarde mag niet negatief zijn')
  .max(99_999_999, 'De waarde is onrealistisch hoog')

const bedrag = z
  .number({ message: 'Vul een geldig bedrag in' })
  .finite('Vul een geldig bedrag in')
  .min(0, 'Het bedrag mag niet negatief zijn')
  .max(99_999_999.99, 'Het bedrag is onrealistisch hoog')

/**
 * Registreren of bijwerken van een afwijking. Eén schema voor het formulier en
 * de API. `afwijkingsBedrag` leeg laten = het systeem berekent het bedrag uit de
 * waarden; ingevuld = handmatige correctie.
 */
export const inkoopAfwijkingInvoerSchema = z
  .object({
    /** Leeg = een afwijking op de hele factuur. */
    regelId: idParam('regelId').nullable().optional(),
    soort: z.enum(INKOOP_AFWIJKING_SOORTEN, { message: 'Kies een soort afwijking' }),
    verwachtWaarde: waarde.nullable().optional(),
    gefactureerdWaarde: waarde.nullable().optional(),
    aantal: waarde.nullable().optional(),
    prijsPerStuk: waarde.nullable().optional(),
    afwijkingsBedrag: bedrag.nullable().optional(),
    toelichting: z.string().trim().max(4000, 'De toelichting is te lang').nullable().optional(),
    /** Leeg = wie de afwijking registreert. */
    eigenaarId: idParam('eigenaarId').nullable().optional(),
    /** Verplicht bij de soort Dubbel: de oorspronkelijke factuur. */
    origineleFactuurId: idParam('origineleFactuurId').nullable().optional(),
    /** De extractie-markering waaruit de afwijking wordt geregistreerd. */
    markering: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.soort === 'DUBBEL' && !d.origineleFactuurId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies de oorspronkelijke factuur waarvan dit een dubbele is.',
        path: ['origineleFactuurId'],
      })
    }
    if (d.soort === 'OVERIG' && (d.toelichting ?? '').trim().length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Geef een toelichting bij een afwijking van de soort Overig.',
        path: ['toelichting'],
      })
    }
  })
export type InkoopAfwijkingInvoer = z.infer<typeof inkoopAfwijkingInvoerSchema>

/**
 * Alleen de eigenaar wijzigen ("neem over"). Een eigen, smal schema: het raakt
 * soort, regel en bedrag nooit, ook niet bij een afwijking waarvan de regel
 * intussen is verdwenen.
 */
export const inkoopAfwijkingEigenaarSchema = z.object({
  eigenaarId: idParam('eigenaarId'),
})
export type InkoopAfwijkingEigenaarInvoer = z.infer<typeof inkoopAfwijkingEigenaarSchema>

/**
 * Een handmatige statusovergang. Elke overgang draagt een reden; OPGELOST
 * vraagt een uitkomst. De uitkomst Creditnota loopt altijd via de actie
 * "Creditnota koppelen", zodat het creditbedrag gecontroleerd wordt.
 */
export const inkoopAfwijkingStatusSchema = z
  .object({
    naar: z.enum(INKOOP_AFWIJKING_STATUSSEN),
    uitkomst: z.enum(INKOOP_AFWIJKING_UITKOMSTEN).nullable().optional(),
    reden: z
      .string()
      .trim()
      .min(3, 'Geef een reden op (minstens 3 tekens).')
      .max(2000, 'De reden is te lang'),
    /** Bij de uitkomst Correctiefactuur: de nieuwe factuur van de leverancier. */
    oplossingFactuurId: idParam('oplossingFactuurId').nullable().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.naar === 'OPGELOST' && !d.uitkomst) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies hoe de afwijking is opgelost.',
        path: ['uitkomst'],
      })
    }
    if (d.naar !== 'OPGELOST' && d.uitkomst) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Een uitkomst hoort alleen bij de status Opgelost.',
        path: ['uitkomst'],
      })
    }
    if (d.uitkomst === 'CREDITNOTA') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Koppel de creditnota via "Creditnota koppelen"; dan wordt het bedrag gecontroleerd.',
        path: ['uitkomst'],
      })
    }
    // Herreview: een correctiefactuur VERVANGT de betwiste factuur. Zonder de
    // koppeling zou de oorspronkelijke factuur weer voluit betaalbaar worden,
    // naast de correctiefactuur.
    if (d.uitkomst === 'CORRECTIEFACTUUR' && !d.oplossingFactuurId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies de correctiefactuur: de factuur die deze factuur vervangt.',
        path: ['oplossingFactuurId'],
      })
    }
    if (d.uitkomst === 'GEACCEPTEERD_MET_REDEN' && d.reden.trim().length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Geef een reden op (minstens 5 tekens).',
        path: ['reden'],
      })
    }
  })
export type InkoopAfwijkingStatusInvoer = z.infer<typeof inkoopAfwijkingStatusSchema>

const mailAdres = z
  .string()
  .trim()
  .toLowerCase()
  .max(320, 'Het e-mailadres is te lang')
  .email('Vul een geldig e-mailadres in.')

/**
 * Melden aan de leverancier: alle open afwijkingen van de factuur in één mail.
 * `ontvangers` leeg = de contacten van de leverancier met de rol "ontvangt
 * afwijkingsmeldingen". Onderwerp en tekst zijn vooraf ingevuld vanuit het
 * beheerbare sjabloon en mogen per melding worden aangepast.
 */
export const inkoopAfwijkingMeldenSchema = z.object({
  ontvangers: z.array(mailAdres).max(10, 'Hoogstens 10 ontvangers').optional(),
  onderwerp: z.string().trim().min(3, 'Vul een onderwerp in').max(300, 'Het onderwerp is te lang').optional(),
  tekst: z.string().trim().min(10, 'Vul de tekst van de melding in').max(10_000, 'De tekst is te lang').optional(),
  factuurBijvoegen: z.boolean().default(true),
  /** Bewijsdocumenten die als bijlage meegaan (leeg = al het bewijs). */
  documentIds: z.array(idParam('documentId')).max(20).optional(),
  /** Herinnering: neemt ook de al gemelde, nog openstaande afwijkingen mee. */
  herinnering: z.boolean().default(false),
})
export type InkoopAfwijkingMeldenInvoer = z.infer<typeof inkoopAfwijkingMeldenSchema>

/**
 * Een creditnota koppelen aan een of meer afwijkingen. Wijkt het creditbedrag
 * af van de som van de afwijkingen, dan weigert de API tot de gebruiker dat
 * bewust bevestigt (met reden); het verschil wordt altijd gemeld.
 */
export const inkoopAfwijkingCreditnotaSchema = z
  .object({
    afwijkingIds: z
      .array(idParam('afwijkingId'))
      .min(1, 'Kies minstens één afwijking.')
      .max(50, 'Hoogstens 50 afwijkingen per creditnota'),
    bevestigAfwijkendBedrag: z.boolean().optional(),
    reden: z.string().trim().max(2000, 'De reden is te lang').optional(),
  })
  .superRefine((d, ctx) => {
    if (d.bevestigAfwijkendBedrag && (d.reden ?? '').trim().length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Geef een reden op waarom het afwijkende bedrag wordt geaccepteerd (minstens 5 tekens).',
        path: ['reden'],
      })
    }
  })
export type InkoopAfwijkingCreditnotaInvoer = z.infer<typeof inkoopAfwijkingCreditnotaSchema>

/** Status-filter van de werkvoorraad: alle openstaande, één status, of alles. */
export const INKOOP_AFWIJKING_STATUS_FILTERS = [
  'OPENSTAAND',
  ...INKOOP_AFWIJKING_STATUSSEN,
  'ALLE',
] as const
export type InkoopAfwijkingStatusFilter = (typeof INKOOP_AFWIJKING_STATUS_FILTERS)[number]

export const inkoopAfwijkingenLijstQuerySchema = z.object({
  leverancierId: idParam('leverancierId').optional(),
  soort: z.enum(INKOOP_AFWIJKING_SOORTEN).optional(),
  status: z.enum(INKOOP_AFWIJKING_STATUS_FILTERS).optional(),
  eigenaarId: idParam('eigenaarId').optional(),
  bron: z.enum(INKOOP_AFWIJKING_BRONNEN).optional(),
  /** Alleen afwijkingen die minstens zoveel dagen zonder reactie openstaan. */
  ouderdomDagen: z.coerce.number().int().min(0).max(3650).optional(),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type InkoopAfwijkingenLijstQuery = z.infer<typeof inkoopAfwijkingenLijstQuerySchema>

export const inkoopAfwijkingInstellingenSchema = z
  .object({
    blokkerendeSoorten: z.array(z.enum(INKOOP_AFWIJKING_SOORTEN)).max(INKOOP_AFWIJKING_SOORTEN.length),
    ouderdomDagen: z
      .number({ message: 'Vul een aantal dagen in' })
      .int('Vul een heel aantal dagen in')
      .min(1, 'Minstens 1 dag')
      .max(365, 'Hoogstens 365 dagen'),
    /** Leeg = het standaardonderwerp. */
    mailOnderwerpSjabloon: z.string().trim().max(300, 'Het onderwerp is te lang').nullable(),
    /** Leeg = de standaardtekst. */
    mailTekstSjabloon: z.string().trim().max(10_000, 'De tekst is te lang').nullable(),
  })
  .superRefine((d, ctx) => {
    if (d.mailOnderwerpSjabloon && !d.mailOnderwerpSjabloon.includes(INKOOP_AFWIJKING_MAIL_REFERENTIE_VELD)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Het onderwerp moet [referentie] bevatten: daarop koppelt het systeem de reactie van de leverancier.',
        path: ['mailOnderwerpSjabloon'],
      })
    }
    if (d.mailTekstSjabloon && !d.mailTekstSjabloon.includes('[afwijkingen]')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'De tekst moet [afwijkingen] bevatten: daar komt de lijst met afwijkingen.',
        path: ['mailTekstSjabloon'],
      })
    }
  })
export type InkoopAfwijkingInstellingenInvoer = z.infer<typeof inkoopAfwijkingInstellingenSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface InkoopAfwijkingDocumentDTO {
  id: string
  soort: InkoopAfwijkingDocumentSoort
  bestandsnaam: string
  mimeType: string
  grootte: number
  omschrijving: string | null
  uploadedByNaam: string | null
  createdAt: string
}

export interface InkoopAfwijkingAuditDTO {
  id: string
  actie: InkoopAfwijkingAuditActie | string
  actorNaam: string | null
  vanStatus: InkoopAfwijkingStatus | null
  naarStatus: InkoopAfwijkingStatus | null
  reden: string | null
  createdAt: string
}

export interface InkoopAfwijkingBerichtDTO {
  id: string
  richting: InkoopAfwijkingBerichtRichting
  meldingReferentie: string | null
  onderwerp: string | null
  adres: string | null
  herinnering: boolean
  /** Status van de uitgaande mail (QUEUED, SENT, DROPPED_ALLOWLIST, ...). */
  mailStatus: string | null
  documentId: string | null
  createdAt: string
}

export interface InkoopAfwijkingDTO {
  id: string
  referentie: string
  factuurId: string
  factuurNr: string
  factuurStatus: string
  leverancier: { id: string; name: string }
  entity: { id: string; name: string } | null
  regelId: string | null
  regelPosition: number | null
  regelOmschrijving: string | null
  soort: InkoopAfwijkingSoort
  status: InkoopAfwijkingStatus
  uitkomst: InkoopAfwijkingUitkomst | null
  bron: InkoopAfwijkingBron
  verwachtWaarde: number | null
  gefactureerdWaarde: number | null
  aantal: number | null
  prijsPerStuk: number | null
  /** Het betwiste bedrag excl. btw. */
  afwijkingsBedrag: number
  bedragHandmatig: boolean
  /** Of deze afwijking (soort + status) nu betaling blokkeert. */
  blokkerend: boolean
  toelichting: string | null
  eigenaarId: string
  eigenaarNaam: string | null
  origineleFactuurId: string | null
  origineleFactuurNr: string | null
  markering: string | null
  intern: boolean
  gemeldOp: string | null
  meldingReferentie: string | null
  laatsteReactieOp: string | null
  /** Hele dagen sinds de laatste activiteit (statusovergang, melding, reactie). */
  dagenZonderReactie: number
  opgelostOp: string | null
  opgelostDoorNaam: string | null
  opgelostReden: string | null
  oplossingFactuurId: string | null
  oplossingFactuurNr: string | null
  oplossingBedrag: number | null
  aantalDocumenten: number
  createdAt: string
  createdByNaam: string | null
}

export interface InkoopAfwijkingDetailDTO extends InkoopAfwijkingDTO {
  documenten: InkoopAfwijkingDocumentDTO[]
  audits: InkoopAfwijkingAuditDTO[]
  berichten: InkoopAfwijkingBerichtDTO[]
}

/** De afgeleide stand van een factuur: "n open afwijkingen, EUR bedrag". */
export interface InkoopAfwijkingenStandDTO {
  aantalOpen: number
  /** Som van de open afwijkingen, excl. btw. */
  openBedrag: number
  aantalBlokkerend: number
  /** Som van de open blokkerende afwijkingen, excl. btw. */
  blokkerendBedrag: number
  /** Het betwiste deel incl. btw (blokkerende afwijkingen). */
  betwistInclBtw: number
  /** Het onbetwiste deel incl. btw: factuurtotaal min het betwiste deel. */
  onbetwistBedrag: number
  factuurTotaal: number
  /** Betalen is geblokkeerd (alleen een deelbetaling van het onbetwiste deel kan). */
  betalenGeblokkeerd: boolean
  /**
   * Een afwijking op deze factuur is opgelost met een correctiefactuur: die
   * vervangt deze factuur. Betalen blijft geblokkeerd tot deze factuur is
   * geannuleerd (ook een deelbetaling kan niet). Null = niet vervangen.
   */
  vervangenDoor: { factuurId: string; factuurNr: string } | null
  /** Wat er al als deelbetaling is vastgelegd (cumulatief). */
  deelbetaald: { bedrag: number; op: string; doorNaam: string | null } | null
  /**
   * Wat er NU nog als deelbetaling kan: het onbetwiste deel min wat al deelbetaald
   * is (nooit negatief). 0 = het onbetwiste deel is al betaald.
   */
  nogDeelTeBetalen: number
  /**
   * Wat er nog te betalen is: het factuurtotaal min een vastgelegde deelbetaling
   * (nooit negatief). Zonder deelbetaling het factuurtotaal.
   */
  restantTeBetalen: number
  /**
   * Het deel van dat restant dat een gekoppelde creditnota dekt (incl. btw): dat
   * wordt met de creditnota verrekend, niet overgemaakt. 0 zonder deelbetaling
   * (dan verrekent de betaallijst de creditnota zelf met de factuur).
   */
  gedektDoorCreditnota: number
  /** De factuur staat op deze concept-betaallijst (nummer), of null. */
  opConceptBetaallijst: string | null
  /**
   * Voor die concept-lijst is al een betaalbestand (SEPA of Excel) gedownload, op
   * dit moment (ISO). Dan kan de factuur al volledig betaald zijn: annuleren van de
   * lijst of een deelbetaling ernaast zou dubbel betalen.
   */
  betaallijstGeexporteerdOp: string | null
}

/** Een voorstel: deze creditnota kan bij deze open afwijking horen. */
export interface InkoopCreditnotaVoorstelDTO {
  afwijking: InkoopAfwijkingDTO
  reden: 'REFERENTIE' | 'FACTUURNUMMER' | 'BEDRAG' | 'LEVERANCIER'
  zekerheid: 'HOOG' | 'MIDDEL' | 'LAAG'
  /** Voorgeselecteerd in het koppelscherm. */
  voorgesteld: boolean
}

export const INKOOP_CREDITNOTA_VOORSTEL_REDEN_LABELS: Record<InkoopCreditnotaVoorstelDTO['reden'], string> = {
  REFERENTIE: 'De afwijkingsreferentie staat op de creditnota',
  FACTUURNUMMER: 'Het factuurnummer staat op de creditnota',
  BEDRAG: 'Het bedrag komt overeen',
  LEVERANCIER: 'Open afwijking bij deze leverancier',
}

/** Alles wat het afwijkingen-paneel op een factuur nodig heeft. */
export interface InkoopFactuurAfwijkingenDTO {
  factuurId: string
  stand: InkoopAfwijkingenStandDTO
  afwijkingen: InkoopAfwijkingDTO[]
  /** De markeringen die als afwijking te registreren zijn (duplicaat, verkeerde BV). */
  markeringVoorstellen: { markering: string; soort: InkoopAfwijkingSoort; alGeregistreerd: boolean }[]
  /** Factuur tussen eigen BV's: melden gaat intern, zonder externe mail. */
  intern: boolean
  /** Deze factuur is een creditnota (negatief bedrag). */
  isCreditnota: boolean
  /** Open afwijkingen van dezelfde leverancier waar deze creditnota bij kan horen. */
  creditnotaVoorstellen: InkoopCreditnotaVoorstelDTO[]
  /** Het creditbedrag (positief, excl. btw) dat nog niet aan afwijkingen is gekoppeld. */
  creditnotaBeschikbaar: number | null
  /**
   * Het aantal open afwijkingen bij deze leverancier (servertotaal). Groter dan
   * het aantal voorstellen = de lijst is afgekapt.
   */
  creditnotaVoorstellenTotaal: number
  blokkerendeSoorten: InkoopAfwijkingSoort[]
}

export type InkoopAfwijkingAdresBron = 'CONTACT_ROL' | 'FACTUURMAIL' | 'RELATIE' | 'CONTACTPERSOON'

export const INKOOP_AFWIJKING_ADRES_BRON_LABELS: Record<InkoopAfwijkingAdresBron, string> = {
  CONTACT_ROL: 'Contact met de rol afwijkingsmeldingen',
  FACTUURMAIL: 'Afzender van de factuurmail',
  RELATIE: 'E-mailadres van de relatie',
  CONTACTPERSOON: 'Contactpersoon van de relatie',
}

export interface InkoopAfwijkingAdresSuggestieDTO {
  adres: string
  bron: InkoopAfwijkingAdresBron
  /** Ingevuld = dit adres wordt afgeraden (eigen domein, no-reply). */
  afgeraden: string | null
  /** Naam van het contact, als het adres van een contactpersoon is. */
  naam: string | null
}

/** Het voorbeeld van de melding, vóór verzenden. */
export interface InkoopAfwijkingMeldingVoorbeeldDTO {
  factuurId: string
  factuurNr: string
  leverancier: { id: string; name: string }
  /** Eigen BV: er gaat geen externe mail, de melding blijft intern. */
  intern: boolean
  referentie: string
  onderwerp: string
  tekst: string
  /** De afwijkingen die in deze melding meegaan. */
  afwijkingen: InkoopAfwijkingDTO[]
  /** Standaardontvangers (contacten met de rol), leeg = de gebruiker kiest. */
  ontvangers: string[]
  suggesties: InkoopAfwijkingAdresSuggestieDTO[]
  /** Geen enkel adres bekend: melden is geblokkeerd tot de relatie een adres heeft. */
  geenAdres: boolean
  bijlagen: { documentId: string | null; bestandsnaam: string; grootte: number; factuur: boolean }[]
}

export interface InkoopAfwijkingMeldenResultaatDTO {
  intern: boolean
  referentie: string
  aantalAfwijkingen: number
  /** Status van de uitgaande mail; null bij een interne melding. */
  mailStatus: string | null
  /** De mail is niet bezorgd (extern verzenden staat uit of het adres is niet toegestaan). */
  nietBezorgd: boolean
  waarschuwing: string | null
}

export interface InkoopAfwijkingenLijstMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Som van de afwijkingsbedragen (excl. btw) over de HELE gefilterde set. */
  totaalBedrag: number
  /** Aantal en bedrag van de nog openstaande afwijkingen in de gefilterde set. */
  aantalOpen: number
  totaalOpenBedrag: number
  /**
   * De eigenaren van de afwijkingen in deze selectie (zonder het eigenaar-filter
   * zelf, zodat de keuzelijst compleet blijft terwijl er één gekozen is).
   */
  eigenaren: { id: string; naam: string; aantal: number }[]
}

export interface InkoopAfwijkingInstellingenDTO {
  blokkerendeSoorten: InkoopAfwijkingSoort[]
  ouderdomDagen: number
  mailOnderwerpSjabloon: string | null
  mailTekstSjabloon: string | null
  /** De standaardteksten uit de code (getoond als de instelling leeg is). */
  standaardOnderwerp: string
  standaardTekst: string
}

/** Leveranciersbeoordeling: afwijkingen van de laatste 12 maanden. */
export interface LeverancierAfwijkingenSamenvattingDTO {
  leverancierId: string
  vanaf: string
  aantal: number
  bedrag: number
  aantalOpen: number
  openBedrag: number
  perSoort: { soort: InkoopAfwijkingSoort; aantal: number; bedrag: number }[]
  /** Contacten die afwijkingsmeldingen ontvangen (naam + adres). */
  ontvangers: { naam: string; email: string }[]
}

/** Foutcodes van de afwijkingen-API die de UI gericht afhandelt. */
export const INKOOP_AFWIJKING_FOUTCODES = {
  /** Betalen geweigerd: er staan blokkerende afwijkingen open. */
  BETALEN_GEBLOKKEERD: 'AFWIJKING_OPEN',
  /** Het creditbedrag wijkt af van de som van de afwijkingen. */
  CREDITBEDRAG_WIJKT_AF: 'CREDITBEDRAG_WIJKT_AF',
  /** De leverancier heeft geen bruikbaar mailadres. */
  GEEN_LEVERANCIER_ADRES: 'GEEN_LEVERANCIER_ADRES',
  /** De statusovergang is niet toegestaan. */
  OVERGANG_NIET_TOEGESTAAN: 'AFWIJKING_OVERGANG_NIET_TOEGESTAAN',
} as const
